import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useTabParam } from '../../shared/ui/useTabParam';
import { useMe } from '../session/SessionProvider';
import { BibleTabs } from './Atlas';
import { bibleLink } from './BiblePage';

export interface MinuteIdea { id: string; ref: string; title: string; body: string; saved: boolean; church?: boolean }
interface ReviewDay { date: string; days: number; done: boolean }
interface StoryProgress { completedOn: string; step: number; dueOn: string | null; due: boolean; mastered: boolean; reviews: number; schedule: ReviewDay[] }
interface StorySummary { id: string; title: string; ref: string; summary: string; lessons: number; church?: boolean; progress: StoryProgress | null }
interface QuizQuestion { id: string; question: string; options: string[]; answer: number }
interface StoryDetail { id: string; title: string; ref: string; summary: string; lessons: { title: string; summary: string; ref: string }[]; quiz: QuizQuestion[]; progress: StoryProgress | null }

const TABS = ['ideas', 'historias'] as const;
const LETTERS = ['A', 'B', 'C', 'D'];

/** «Éxodo 3-14» abre el primer capítulo (el lector no recibe rangos de capítulos). */
export const minuteLink = (ref: string) => bibleLink(ref.replace(/^(.+?\s\d+)-\d+$/, '$1'));

/** Fecha corta: «mié 4 nov». */
const shortDate = (iso: string) =>
  new Intl.DateTimeFormat('es-MX', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`));

/** La cita siempre en dorado y enlazada a la Biblia ya cargada (no se copia el versículo). */
function Citation({ refText }: { refText: string }) {
  return (
    <Link to={minuteLink(refText)} className="minutes__ref">
      {refText}
    </Link>
  );
}

/** Biblia en minutos: ideas del día y la historias con repaso, como pestaña de la Biblia. */
export function MinutesPage() {
  const me = useMe();
  const [tab, setTab] = useTabParam(TABS);
  return (
    <div className="page page--narrow bible-wide minutes">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="minutos" />
      <div className="minutes__intro">
        <h2>Biblia en minutos</h2>
        <p className="small muted">Ideas cortas y las historias de la Biblia, con repaso para que no se olviden. Toca la cita para leerla completa.</p>
      </div>
      <div className="minutes__switch" role="tablist" aria-label="Biblia en minutos">
        {TABS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={`minutes__switch-btn minutes__switch-btn--${t}${tab === t ? ' minutes__switch-btn--on' : ''}`} onClick={() => setTab(t)}>
            {t === 'ideas' ? 'Ideas del día' : 'Historias'}
          </button>
        ))}
      </div>
      {tab === 'ideas' ? <IdeasTab /> : <StoriesTab />}
      {me.activeChurchId && me.can('devotional.manage') ? <ChurchUsage churchId={me.activeChurchId} /> : null}
      {me.activeChurchId && me.can('devotional.manage') ? (
        <p className="small">
          <Link to="/app/biblia/minutos/iglesia">Agregar ideas e historias de mi iglesia</Link>
        </p>
      ) : null}
      {me.isPlatformAdmin ? (
        <p className="small">
          <Link to="/app/biblia/minutos/contenido">Administrar el contenido de KEORA</Link>
        </p>
      ) : null}
    </div>
  );
}

// ── Ideas del día ──

function IdeasTab() {
  const data = useAsync((signal) => api.get<{ date: string; items: MinuteIdea[]; savedCount: number }>('/bible/minutes/ideas/today', signal), []);
  const [i, setI] = useState(0);
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [count, setCount] = useState<number | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const items = data.data.items;
  const savedCount = count ?? data.data.savedCount;
  if (showSaved) return <SavedIdeas onBack={(n) => (setShowSaved(false), setCount(n), setSaved({}), data.reload())} />;
  const counter = (
    <button type="button" className="minutes__saved-count" onClick={() => setShowSaved(true)}>
      Guardadas: {savedCount}
    </button>
  );
  if (items.length === 0) return <EmptyState title="Todavía no hay ideas">Pronto habrá ideas nuevas cada día.</EmptyState>;
  if (i >= items.length) {
    return (
      <div className="minutes__done">
        <div className="minutes__toolbar">{counter}</div>
        <Card>
          <p className="minutes__done-title">Terminaste las ideas de hoy</p>
          <p className="small muted">Mañana habrá más. Mientras, puedes repasar las que guardaste o leer una historia.</p>
          <div className="actions actions--wrap">
            <Button onClick={() => setI(0)}>Empezar de nuevo</Button>
            <Button variant="secondary" onClick={() => setShowSaved(true)}>
              Ver mis guardadas
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  const idea = items[i]!;
  const isSaved = saved[idea.id] ?? idea.saved;
  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      const r = isSaved ? await api.delete<{ savedCount: number }>(`/bible/me/minutes/saved/${idea.id}`) : await api.put<{ savedCount: number }>(`/bible/me/minutes/saved/${idea.id}`, {});
      setSaved((s) => ({ ...s, [idea.id]: !isSaved }));
      setCount(r.savedCount);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="Ideas del día">
      <div className="minutes__toolbar">
        <span className="small muted">
          Idea {i + 1} de {items.length}
        </span>
        {counter}
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <article key={idea.id} className="minutes__card minutes__card--idea" aria-live="polite">
        <div className="minutes__meta">
          <Citation refText={idea.ref} />
          {idea.church ? <span className="badge">De tu iglesia</span> : null}
        </div>
        <h3 className="minutes__title">{idea.title}</h3>
        <p className="minutes__body">{idea.body}</p>
      </article>
      <div className="actions minutes__actions">
        <Button variant="secondary" busy={busy} onClick={() => void toggle()} aria-pressed={isSaved}>
          {isSaved ? '✓ Guardada' : 'Guardar idea'}
        </Button>
        <Button onClick={() => setI(i + 1)}>Siguiente</Button>
      </div>
      <Dots total={items.length} current={i} />
    </section>
  );
}

function Dots({ total, current }: { total: number; current: number }) {
  return (
    <ol className="minutes__dots" aria-label={`Progreso: ${current + 1} de ${total}`}>
      {Array.from({ length: total }, (_, k) => (
        <li key={k} className={`minutes__dot${k < current ? ' minutes__dot--past' : ''}${k === current ? ' minutes__dot--on' : ''}`} />
      ))}
    </ol>
  );
}

function SavedIdeas({ onBack }: { onBack: (count: number) => void }) {
  const list = useAsync((signal) => api.get<{ items: MinuteIdea[] }>('/bible/me/minutes/saved', signal).then((r) => r.items), []);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const items = list.status === 'ready' ? list.data.filter((x) => !removed.has(x.id)) : [];
  return (
    <section aria-label="Mis ideas guardadas">
      <div className="minutes__toolbar">
        <button type="button" className="btn btn--ghost" onClick={() => onBack(items.length)}>
          ← Volver a las ideas
        </button>
      </div>
      <h3 className="minutes__subtitle">Mis ideas guardadas</h3>
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' && items.length === 0 ? <EmptyState title="Aún no guardas ideas">Toca «Guardar idea» en las que quieras volver a leer.</EmptyState> : null}
      <ul className="minutes__saved">
        {items.map((x) => (
          <li key={x.id} className="minutes__card minutes__card--idea minutes__card--still">
            <Citation refText={x.ref} />
            <h4 className="minutes__title">{x.title}</h4>
            <p className="minutes__body">{x.body}</p>
            <button type="button" className="btn btn--ghost" onClick={() => void api.delete(`/bible/me/minutes/saved/${x.id}`).then(() => setRemoved((s) => new Set(s).add(x.id)))}>
              Quitar de guardadas
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Historias ──

function StoriesTab() {
  const list = useAsync((signal) => api.get<{ items: StorySummary[]; total: number; completed: number; due: number }>('/bible/minutes/stories', signal), []);
  const [open, setOpen] = useState<string | null>(null);
  if (open) return <StoryPlayer id={open} onExit={() => (setOpen(null), list.reload())} />;
  if (list.status === 'loading') return <Loading />;
  if (list.status === 'error') return <ErrorState error={list.error} onRetry={list.reload} />;
  const { items, total, completed, due } = list.data;
  if (items.length === 0) return <EmptyState title="Todavía no hay historias">Pronto habrá historias para aprender.</EmptyState>;
  return (
    <section aria-label="Historias">
      <div className="minutes__toolbar">
        <span className="minutes__completed">
          Completadas: {completed}/{total}
        </span>
        {due > 0 ? <span className="badge badge--accent">{due === 1 ? '1 repaso hoy' : `${due} repasos hoy`}</span> : null}
      </div>
      <ul className="minutes__grid">
        {items.map((s) => (
          <li key={s.id}>
            <button type="button" className={`minutes__story${s.progress ? ' minutes__story--done' : ''}`} onClick={() => setOpen(s.id)}>
              <span className="minutes__story-title">
                {s.title} {s.church ? <span className="badge">De tu iglesia</span> : null}
              </span>
              <span className="minutes__ref minutes__ref--plain">{s.ref}</span>
              <span className="small muted">{s.summary}</span>
              <span className="minutes__story-state small">
                {!s.progress
                  ? `${s.lessons} lecciones · 3 preguntas`
                  : s.progress.mastered
                    ? '✓ Aprendida: terminaste los cuatro repasos'
                    : s.progress.due
                      ? '↻ Toca repasar hoy'
                      : `✓ Repaso programado · ${shortDate(s.progress.dueOn!)}`}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StoryPlayer({ id, onExit }: { id: string; onExit: () => void }) {
  const data = useAsync((signal) => api.get<StoryDetail>(`/bible/minutes/stories/${id}`, signal), [id]);
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [result, setResult] = useState<{ firstTime: boolean; reviewed: boolean; progress: StoryProgress } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Si ya la completó, va directo al repaso (sin volver a las lecciones).
  useEffect(() => {
    if (data.status === 'ready' && data.data.progress) setStep(data.data.lessons.length);
  }, [data.status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const s = data.data;
  const lessonsN = s.lessons.length;
  const totalSteps = lessonsN + s.quiz.length;
  const back = (
    <div className="minutes__toolbar">
      <button type="button" className="btn btn--ghost" onClick={onExit}>
        ← Historias
      </button>
    </div>
  );

  if (result) {
    const missed = s.quiz.filter((q) => picked[q.id] !== q.answer).length;
    return (
      <section aria-label="Historia completada">
        {back}
        <Card>
          <p className="minutes__done-title">{result.firstTime ? 'Historia completada' : result.reviewed ? 'Repaso hecho' : 'Práctica terminada'}</p>
          <p className="small muted">
            {s.title} · {s.quiz.length - missed} de {s.quiz.length} correctas
          </p>
          {result.progress.mastered ? (
            <p>✓ Terminaste los cuatro repasos. ¡Esta historia ya es tuya!</p>
          ) : (
            <>
              <p className="minutes__scheduled">✓ Repaso programado</p>
              <ol className="minutes__calendar" aria-label="Calendario de repaso">
                {result.progress.schedule.map((d) => (
                  <li key={d.days} className={`minutes__day${d.done ? ' minutes__day--done' : ''}${d.date === result.progress.dueOn ? ' minutes__day--next' : ''}`}>
                    <span className="minutes__day-n">Día {d.days}</span>
                    <span className="small">{shortDate(d.date)}</span>
                  </li>
                ))}
              </ol>
              {result.progress.dueOn && !result.progress.schedule.some((d) => d.date === result.progress.dueOn) ? (
                <p className="small muted">Siguiente repaso: {shortDate(result.progress.dueOn)}.</p>
              ) : null}
              <p className="small muted">Te avisaremos el día que toque repasar.</p>
            </>
          )}
          <div className="actions">
            <Button onClick={onExit}>Ver historias</Button>
          </div>
        </Card>
      </section>
    );
  }

  const percent = Math.round((Math.min(step, totalSteps) / totalSteps) * 100);
  const bar = (
    <div className="minutes__progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label="Avance de la historia">
      <span style={{ width: `${percent}%` }} />
    </div>
  );

  if (step < lessonsN) {
    const l = s.lessons[step]!;
    return (
      <section aria-label={s.title}>
        {back}
        {bar}
        <p className="small muted">
          {s.title} · Lección {step + 1} de {lessonsN}
        </p>
        <article key={step} className="minutes__card minutes__card--story" aria-live="polite">
          <Citation refText={l.ref} />
          <h3 className="minutes__title">{l.title}</h3>
          <p className="minutes__body">{l.summary}</p>
        </article>
        <div className="actions minutes__actions">
          {step > 0 ? (
            <Button variant="secondary" onClick={() => setStep(step - 1)}>
              Anterior
            </Button>
          ) : null}
          <Button onClick={() => setStep(step + 1)}>{step + 1 < lessonsN ? 'Siguiente lección' : 'Ir a las preguntas'}</Button>
        </div>
      </section>
    );
  }

  const qi = step - lessonsN;
  const q = s.quiz[qi]!;
  const choice = picked[q.id];
  const answered = choice !== undefined;
  const last = qi + 1 >= s.quiz.length;
  async function finish() {
    setError(null);
    try {
      const asked = s.quiz.map((x) => x.id);
      const missed = s.quiz.filter((x) => picked[x.id] !== x.answer).map((x) => x.id);
      setResult(await api.post(`/bible/me/minutes/stories/${id}/answers`, { asked, missed }));
    } catch (err) {
      setError(errorMessage(err));
    }
  }
  return (
    <section aria-label={`Preguntas de ${s.title}`}>
      {back}
      {bar}
      <p className="small muted">
        {s.progress?.due ? 'Repaso' : 'Pregunta'} {qi + 1} de {s.quiz.length}
      </p>
      {error ? <Alert>{error}</Alert> : null}
      <article key={q.id} className="minutes__card minutes__card--story">
        <h3 className="minutes__title">{q.question}</h3>
        <div className="minutes__options">
          {q.options.map((o, k) => (
            <button
              key={k}
              type="button"
              className={`minutes__option${answered && k === q.answer ? ' minutes__option--right' : ''}${answered && choice === k && k !== q.answer ? ' minutes__option--wrong' : ''}`}
              disabled={answered}
              onClick={() => setPicked((p) => ({ ...p, [q.id]: k }))}
            >
              <span className="minutes__letter">{LETTERS[k]}</span> {o}
            </button>
          ))}
        </div>
        {answered ? (
          <p className={`minutes__feedback ${choice === q.answer ? 'minutes__feedback--right' : 'minutes__feedback--wrong'}`} role="status">
            {choice === q.answer ? '¡Correcto!' : 'Casi. Esta pregunta volverá antes en tu repaso.'}
          </p>
        ) : null}
      </article>
      {answered ? (
        <div className="actions minutes__actions">
          <Button onClick={() => (last ? void finish() : setStep(step + 1))}>{last ? 'Terminar' : 'Siguiente pregunta'}</Button>
        </div>
      ) : null}
    </section>
  );
}

// ── Para el pastor: solo números de conjunto ──

function ChurchUsage({ churchId }: { churchId: string }) {
  const [open, setOpen] = useState(false);
  const stats = useAsync(
    (signal) =>
      open
        ? api.get<{ people: number; saved: number; completions: number; reviews: number; active: number; stories: { title: string; completed: number; mastered: number }[] }>(`/bible/churches/${churchId}/minutes/stats`, signal)
        : Promise.resolve(null),
    [open, churchId],
  );
  return (
    <details className="minutes__usage" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary>¿Cuántos lo usan en mi iglesia?</summary>
      {stats.status === 'loading' && open ? <Loading /> : null}
      {stats.status === 'error' ? <ErrorState error={stats.error} onRetry={stats.reload} /> : null}
      {stats.status === 'ready' && stats.data ? (
        <div className="small">
          <p>
            {stats.data.people} {stats.data.people === 1 ? 'persona lo ha usado' : 'personas lo han usado'} · {stats.data.active} activas en los últimos 30 días · {stats.data.completions} historias completadas ·{' '}
            {stats.data.reviews} repasos · {stats.data.saved} ideas guardadas
          </p>
          <ul className="list">
            {stats.data.stories.map((x) => (
              <li key={x.title} className="list__item">
                {x.title}: {x.completed} la completaron, {x.mastered} terminaron los repasos
              </li>
            ))}
          </ul>
          <p className="muted">Solo se muestran totales: nadie ve quién contestó ni qué contestó.</p>
        </div>
      ) : null}
    </details>
  );
}
